import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Factory, Globe2, Leaf, Quote } from "lucide-react";
import { MEDIA, PRODUCTS, SITE, TESTIMONIALS } from "../lib/site";
import Reveal from "../components/Reveal";

const STATS = [
    { value: "10+", label: "Years of craft" },
    { value: "5+", label: "Countries served" },
    { value: "100K+", label: "MT capacity / yr" },
    { value: "0", label: "Defect tolerance" },
];

const Home = () => {
    return (
        <div data-testid="home-page" className="bg-bone">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 lg:pt-24 lg:pb-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                        <div className="lg:col-span-7 relative z-10">
                            <Reveal>
                                <div className="text-xs uppercase tracking-widest2 text-charcoal/60 mb-6 flex items-center gap-3">
                                    <span className="inline-block w-8 h-px bg-forest" /> {SITE.estd} · Made in India
                                </div>
                            </Reveal>
                            <Reveal delay={120}>
                                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-light leading-[0.95] tracking-tight text-forest text-balance">
                                    Premium Synthetic Rattan<br />
                                    and Ropes, crafted for<br />
                                    <span className="italic text-copper">exceptional outdoor</span> living.
                                </h1>
                            </Reveal>
                            <Reveal delay={250}>
                                <p className="mt-8 text-lg text-charcoal/70 max-w-xl leading-relaxed">
                                    Shaurya Polymers manufactures premium PE wicker, all-weather synthetic rattan, and outdoor ropes —
                                    engineered in India, woven into furniture across multiple countries.
                                </p>
                            </Reveal>
                            <Reveal delay={380}>
                                <div className="mt-10 flex flex-wrap items-center gap-5">
                                    <Link
                                        to="/services"
                                        data-testid="hero-cta-products"
                                        className="group inline-flex items-center gap-3 px-7 py-4 bg-forest text-bone hover:bg-charcoal transition-colors"
                                    >
                                        Explore the catalogue
                                        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                    </Link>
                                    <Link
                                        to="/contact"
                                        data-testid="hero-cta-contact"
                                        className="inline-flex items-center gap-2 text-sm tracking-wide text-forest underline-grow"
                                    >
                                        Speak with our team →
                                    </Link>
                                </div>
                            </Reveal>
                        </div>

                        <div className="lg:col-span-5 relative">
                            <Reveal delay={200}>
                                <div className="relative aspect-[4/5] w-full overflow-hidden border border-line">
                                    <img
                                        src={MEDIA.heroWicker}
                                        alt="PE wicker macro texture"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 ring-1 ring-inset ring-forest/5" />
                                </div>
                                <div className="absolute -bottom-6 -left-6 hidden lg:block bg-bone border border-line px-5 py-4 max-w-[220px]">
                                    <div className="text-[10px] uppercase tracking-widest2 text-charcoal/60">Material</div>
                                    <div className="font-serif text-xl text-forest mt-1">PE Wicker Round</div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>

                {/* Marquee */}
                <div className="border-y border-line bg-surface-alt py-6 overflow-hidden">
                    <div className="flex gap-16 whitespace-nowrap animate-marquee">
                        {[
                            "PE Wicker", "All-Weather Synthetic Rattan", "Outdoor Ropes", "UV Stabilized", "Export Grade",
                            "PE Wicker", "All-Weather Synthetic Rattan", "Outdoor Ropes", "UV Stabilized", "Export Grade",
                        ].map((t, i) => (
                            <span key={i} className="font-serif italic text-3xl md:text-4xl text-forest/60">
                                {t} <span className="text-sand mx-6">◇</span>
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="py-24 lg:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <div className="text-xs uppercase tracking-widest2 text-copper mb-6">— On Origin</div>
                            <h2 className="font-serif text-4xl lg:text-5xl text-forest leading-tight">
                                A manufacturing house, run like an atelier.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <Reveal delay={150}>
                            <p className="text-lg text-charcoal/75 leading-relaxed">
                                Founded in 2014, Shaurya Polymers grew from a single extruder into a quiet powerhouse for the
                                global outdoor furniture industry. We built our reputation on three uncompromising principles —
                                consistent batches, true-to-swatch colour, and weave-friendly profiles that designers can rely on.
                            </p>
                            <p className="mt-6 text-lg text-charcoal/75 leading-relaxed">
                                Today, our materials sit on terraces in resorts, lounges in hospitality projects, and
                                outdoor spaces across global markets. Every roll leaves the floor having passed through human hands.
                            </p>
                            <Link to="/about" data-testid="home-about-cta" className="mt-8 inline-flex items-center gap-2 text-forest underline-grow">
                                Read our story <ArrowRight size={14} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-16 bg-forest text-bone relative overflow-hidden grain-overlay">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
                    {STATS.map((s, i) => (
                        <Reveal key={i} delay={i * 100}>
                            <div>
                                <div className="font-serif text-5xl lg:text-6xl text-sand">{s.value}</div>
                                <div className="mt-2 text-xs uppercase tracking-widest2 text-bone/70">{s.label}</div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PRODUCTS */}
            <section className="py-24 lg:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
                            <div>
                                <div className="text-xs uppercase tracking-widest2 text-copper mb-4">— The Catalogue</div>
                                <h2 className="font-serif text-4xl lg:text-5xl text-forest max-w-xl leading-tight">
                                    Three material families, infinite expression.
                                </h2>
                            </div>
                            <Link to="/services" data-testid="home-products-cta" className="text-sm text-forest underline-grow">
                                View full catalogue →
                            </Link>
                        </div>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
                        {PRODUCTS.map((p, i) => (
                            <Reveal key={p.id} delay={i * 120}>
                                <Link
                                    to="/services"
                                    data-testid={`product-card-${p.id}`}
                                    className="group block border border-line hover:border-forest transition-colors duration-500"
                                >
                                    <div className="aspect-[4/5] overflow-hidden bg-surface-alt">
                                        <img
                                            src={p.image}
                                            alt={p.name}
                                            className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="p-6 lg:p-8">
                                        <div className="text-[10px] uppercase tracking-widest2 text-copper">{p.kicker}</div>
                                        <h3 className="font-serif text-3xl text-forest mt-3">{p.name}</h3>
                                        <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">{p.summary}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="py-24 bg-surface-alt border-y border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
                    <Reveal>
                        <div className="text-xs uppercase tracking-widest2 text-copper mb-6">— Why Shaurya</div>
                        <h2 className="font-serif text-4xl lg:text-5xl text-forest leading-tight">
                            Engineered to endure. Crafted to be desired.
                        </h2>
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                            {[
                                { Icon: Factory, t: "In-house extrusion", d: "Vertical control over polymer, color, and profile — every step under one roof." },
                                { Icon: Award, t: "Batch consistency", d: "Lab-tested for color and gauge so your weave is identical, lot after lot." },
                                { Icon: Globe2, t: "Export ready", d: "Sea & air shipments to multiple countries with full documentation support." },
                                { Icon: Leaf, t: "Sustainable polymers", d: "UV-stabilised, recyclable, and engineered for long outdoor life." },
                            ].map((f, i) => (
                                <div key={i} className="flex flex-col">
                                    <f.Icon size={26} className="text-copper" strokeWidth={1.5} />
                                    <div className="font-serif text-2xl text-forest mt-4">{f.t}</div>
                                    <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{f.d}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-24 lg:py-32 bg-forest text-bone relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="text-xs uppercase tracking-widest2 text-sand mb-6">— Voices from the trade</div>
                        <h2 className="font-serif text-4xl lg:text-5xl max-w-2xl leading-tight">
                            What our partners quietly say.
                        </h2>
                    </Reveal>

                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
                        {TESTIMONIALS.map((t, i) => (
                            <Reveal key={i} delay={i * 150}>
                                <div data-testid={`testimonial-${i}`} className="border-t border-bone/15 pt-8">
                                    <Quote size={28} className="text-sand mb-6" strokeWidth={1.5} />
                                    <p className="font-serif italic text-2xl leading-snug text-bone/95">
                                        "{t.quote}"
                                    </p>
                                    <div className="mt-8">
                                        <div className="text-sm text-bone">{t.author}</div>
                                        <div className="text-xs uppercase tracking-widest2 text-bone/50 mt-1">{t.org}</div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="bg-surface-alt border border-line p-10 md:p-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        <Reveal>
                            <div className="text-xs uppercase tracking-widest2 text-copper mb-4">— Begin a project</div>
                            <h3 className="font-serif text-3xl md:text-4xl text-forest leading-tight">
                                Tell us what you're weaving. We'll send swatches.
                            </h3>
                        </Reveal>
                        <Reveal delay={150}>
                            <div className="flex flex-wrap gap-5 md:justify-end">
                                <Link
                                    to="/contact"
                                    data-testid="home-bottom-cta"
                                    className="inline-flex items-center gap-3 px-7 py-4 bg-forest text-bone hover:bg-charcoal transition-colors"
                                >
                                    Request a quote <ArrowRight size={16} />
                                </Link>
                                <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-2 px-7 py-4 border border-forest text-forest hover:bg-forest hover:text-bone transition-colors">
                                    Email sales
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
